import { useCallback, useEffect, useRef, useState } from 'react'
import { useSession } from '@/context/AuthContext'
import { useToast } from '@/context/ToastContext'
import type { ContentPlan } from '@/lib/types'
import type { Visibility } from '@/lib/roles'

// Cache per-user untuk paint instan (pola sama dengan AppContext).
// Diekspor agar handler logout (AppSidebar) ikut membersihkannya — cegah data
// bocor antar akun di browser yang sama.
export const CONTENT_PLANS_CACHE_PREFIX = 'jal_content_plans_'
const CACHE_PREFIX = CONTENT_PLANS_CACHE_PREFIX

function sortPlans(list: ContentPlan[]): ContentPlan[] {
  return [...list].sort((a, b) =>
    a.planDate === b.planDate
      ? a.createdAt.localeCompare(b.createdAt)
      : a.planDate.localeCompare(b.planDate),
  )
}

function readCache(key: string | null): ContentPlan[] | null {
  if (!key || typeof window === 'undefined') return null
  try { const raw = localStorage.getItem(key); return raw ? (JSON.parse(raw) as ContentPlan[]) : null } catch { return null }
}
function writeCache(key: string | null, data: ContentPlan[]): void {
  if (!key || typeof window === 'undefined') return
  try { localStorage.setItem(key, JSON.stringify(data)) } catch { /* abaikan */ }
}

export interface NewContentPlan {
  title: string
  planDate: string
  status?: ContentPlan['status']
  contentBank?: string[]
  funnel?: ContentPlan['funnel']
  notes?: string
  color?: string
  department?: string | null
  pic?: string[]
  projectId?: string | null
  /** Siapa yang boleh melihat konten ini (default 'public'). */
  visibility?: Visibility
  /** Mode 'custom': daftar email yang boleh melihat. */
  viewerEmails?: string[]
}

export interface UseContentPlans {
  plans: ContentPlan[]
  loading: boolean
  error: string | null
  reload: () => Promise<void>
  createPlan: (data: NewContentPlan) => Promise<ContentPlan | null>
  updatePlan: (id: string, updates: Partial<NewContentPlan>) => Promise<void>
  deletePlan: (id: string) => Promise<void>
}

/**
 * Sumber data tunggal untuk Content Plan & Calendar Plan. Keduanya membaca/menulis
 * tabel `content_plans` yang sama → selalu sinkron lewat database.
 */
export function useContentPlans(): UseContentPlans {
  const { data: session } = useSession()
  const { toast } = useToast()
  const cacheKey = session?.user?.email ? `${CACHE_PREFIX}${session.user.email.toLowerCase()}` : null

  const [plans, setPlans] = useState<ContentPlan[]>(() => readCache(cacheKey) ?? [])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const plansRef = useRef(plans)
  useEffect(() => { plansRef.current = plans }, [plans])

  const apply = useCallback((next: ContentPlan[]) => {
    const sorted = sortPlans(next)
    setPlans(sorted)
    writeCache(cacheKey, sorted)
  }, [cacheKey])

  const reload = useCallback(async () => {
    try {
      const res = await fetch('/api/content-plans')
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body?.error || 'Gagal memuat konten')
      }
      const data = await res.json()
      apply(Array.isArray(data) ? data : [])
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memuat konten')
    } finally {
      setLoading(false)
    }
  }, [apply])

  useEffect(() => {
    const cached = readCache(cacheKey)
    if (cached) { setPlans(sortPlans(cached)); setLoading(false) }
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey])

  const createPlan = useCallback(async (data: NewContentPlan): Promise<ContentPlan | null> => {
    try {
      const res = await fetch('/api/content-plans', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      const plan = await res.json()
      if (!res.ok || !plan?.id) throw new Error(plan?.error || 'Gagal membuat konten')
      apply([...plansRef.current, plan])
      toast('Konten ditambahkan.', 'success')
      return plan
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Gagal membuat konten', 'error')
      return null
    }
  }, [apply, toast])

  const updatePlan = useCallback(async (id: string, updates: Partial<NewContentPlan>) => {
    const prev = plansRef.current
    // Optimistic.
    apply(prev.map((p) => (p.id === id ? { ...p, ...updates } as ContentPlan : p)))
    try {
      const res = await fetch(`/api/content-plans/${id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      })
      const plan = await res.json()
      if (!res.ok || !plan?.id) throw new Error(plan?.error || 'Gagal menyimpan')
      apply(plansRef.current.map((p) => (p.id === id ? plan : p)))
    } catch (e) {
      apply(prev) // rollback
      toast(e instanceof Error ? e.message : 'Gagal menyimpan', 'error')
    }
  }, [apply, toast])

  const deletePlan = useCallback(async (id: string) => {
    const prev = plansRef.current
    apply(prev.filter((p) => p.id !== id))
    try {
      const res = await fetch(`/api/content-plans/${id}`, { method: 'DELETE' })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body?.error || 'Gagal menghapus')
      }
      toast('Konten dihapus.', 'success')
    } catch (e) {
      apply(prev)
      toast(e instanceof Error ? e.message : 'Gagal menghapus', 'error')
    }
  }, [apply, toast])

  return { plans, loading, error, reload, createPlan, updatePlan, deletePlan }
}
